/**
 * Named, seeded RNG streams — the determinism rule from AoD §13 and §1 goal 6.
 *
 * Every roll in Annwn comes from a stream forked by name from the world seed.
 * Two properties fall out of that, and both are load-bearing:
 *
 * 1. **Replayability.** Same seed + same intent script ⇒ same world, same
 *    fight, same log. A bug report is a seed and a script, not a video.
 * 2. **Independence.** Drawing from `combat:battleId` does not perturb
 *    `weather`. Systems can be added, reordered or skipped without shifting
 *    the dice under every other system — which is what makes a growing
 *    simulation debuggable at all.
 *
 * Stream *positions* are part of world state, so a world saved mid-fight and
 * reloaded resumes the same sequence. The engine mutates those positions in
 * place on a draft (see `advance`); nothing outside the sim should hold a
 * `Rng` across ticks.
 *
 * The generator is sfc32 (Chris Doty-Humphrey's "Small Fast Counting"),
 * seeded via xmur3. Chosen because it is 32-bit-integer-only: `Math.imul`,
 * `|0`, `>>>` and `<<` produce bit-identical results on every JS engine and
 * every OS, which `Math.random` and anything float-based cannot promise.
 */
/**
 * Well-known stream names. Not exhaustive and not enforced — battle streams
 * are per-battle (`combat:${battleId}`) — but sharing constants keeps typos
 * from silently forking a brand new stream.
 */
export const RNG_STREAM = {
    worldgen: 'worldgen',
    weather: 'weather',
    infection: 'infection',
    wounds: 'wounds',
    narration: 'narration',
    wildlife: 'wildlife',
};
/** 2^32, as a float. Every uint32 below is exactly representable. */
const UINT32_RANGE = 4294967296;
/** sfc32 needs a few rounds before its output avalanches. */
const WARMUP_DRAWS = 15;
/**
 * xmur3 — expands a string into a sequence of well-mixed 32-bit seeds.
 * Integer-only, so identical across engines.
 */
function xmur3(input) {
    let h = 1779033703 ^ input.length;
    for (let i = 0; i < input.length; i += 1) {
        h = Math.imul(h ^ input.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return () => {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        h ^= h >>> 16;
        return h >>> 0;
    };
}
/**
 * One sfc32 step, advancing `state` in place and returning a uint32.
 * All arithmetic is `|0`/`>>>`-clamped: no float ever enters the state.
 */
function step(state) {
    const { a, b, c, d } = state;
    const t = (((a + b) | 0) + d) | 0;
    state.d = (d + 1) | 0;
    state.a = b ^ (b >>> 9);
    state.b = (c + (c << 3)) | 0;
    state.c = (((c << 21) | (c >>> 11)) + t) | 0;
    state.draws += 1;
    return t >>> 0;
}
/** Seed a fresh stream position from an arbitrary string. */
export function seedStream(seed) {
    const next = xmur3(seed);
    const state = { a: next(), b: next(), c: next(), d: next(), draws: 0 };
    for (let i = 0; i < WARMUP_DRAWS; i += 1)
        step(state);
    state.draws = 0;
    return state;
}
/**
 * Fork a named stream from a world seed. The NUL separator keeps
 * `('ab', 'c')` from colliding with `('a', 'bc')`.
 */
export function forkStream(worldSeed, name) {
    return seedStream(`${worldSeed} ${name}`);
}
/** Copy stream positions — used when cloning world state. */
export function cloneStreams(streams) {
    const copy = {};
    for (const name of Object.keys(streams)) {
        copy[name] = { ...streams[name] };
    }
    return copy;
}
/**
 * A handle onto one stream. Drawing advances the underlying `RngStreamState`,
 * which is why handles are handed out per-tick by the engine rather than
 * stored: whoever holds one can move the world's dice.
 */
export class Rng {
    name;
    state;
    constructor(name, state) {
        this.name = name;
        this.state = state;
    }
    /** How many words this stream has drawn since the world began. */
    get draws() {
        return this.state.draws;
    }
    /** A raw 32-bit word. The primitive everything else is built from. */
    nextUint32() {
        return step(this.state);
    }
    /** A float in [0, 1). Exact: a uint32 divided by 2^32 loses no bits. */
    next() {
        return step(this.state) / UINT32_RANGE;
    }
    /**
     * An integer in [minInclusive, maxExclusive), uniformly.
     *
     * Rejection-sampled rather than `% range`, which would bias the low end of
     * any range that does not divide 2^32. Combat cares: a 1-in-20 that is
     * really 1-in-19.97 is the kind of thing that never shows up in a test but
     * does show up in a tuning pass.
     */
    int(minInclusive, maxExclusive) {
        if (!Number.isInteger(minInclusive) || !Number.isInteger(maxExclusive)) {
            throw new TypeError(`rng('${this.name}').int expects integers`);
        }
        const range = maxExclusive - minInclusive;
        if (range <= 0) {
            throw new RangeError(`rng('${this.name}').int needs max > min (got ${minInclusive}, ${maxExclusive})`);
        }
        if (range > UINT32_RANGE) {
            throw new RangeError(`rng('${this.name}').int range must fit in 32 bits`);
        }
        // Discard the short tail at the top of the uint32 space that would
        // otherwise over-represent the first `2^32 % range` values.
        const limit = UINT32_RANGE - (UINT32_RANGE % range);
        let value = step(this.state);
        while (value >= limit)
            value = step(this.state);
        return minInclusive + (value % range);
    }
    /** A die: 1..sides inclusive. */
    roll(sides) {
        return this.int(1, sides + 1);
    }
    /** A float in [min, max). */
    range(min, max) {
        return min + this.next() * (max - min);
    }
    /** True with probability `p`. `chance(0)` never fires, `chance(1)` always. */
    chance(p) {
        if (p <= 0)
            return false;
        if (p >= 1)
            return true;
        return this.next() < p;
    }
    /** Uniform choice from a non-empty array. */
    pick(items) {
        if (items.length === 0) {
            throw new RangeError(`rng('${this.name}').pick needs a non-empty array`);
        }
        return items[this.int(0, items.length)];
    }
    /**
     * Weighted choice. `weight` must be non-negative; entries weighted 0 are
     * never picked. Used for wound-location zone weighting (AoD §4.2) and
     * narration template pools (§12.1).
     */
    pickWeighted(items, weight) {
        if (items.length === 0) {
            throw new RangeError(`rng('${this.name}').pickWeighted needs a non-empty array`);
        }
        let total = 0;
        const weights = items.map((item) => {
            const w = weight(item);
            if (!(w >= 0) || !Number.isFinite(w)) {
                throw new RangeError(`rng('${this.name}').pickWeighted got a bad weight: ${w}`);
            }
            total += w;
            return w;
        });
        if (total <= 0) {
            throw new RangeError(`rng('${this.name}').pickWeighted needs at least one positive weight`);
        }
        let roll = this.next() * total;
        for (let i = 0; i < items.length; i += 1) {
            roll -= weights[i];
            if (roll < 0)
                return items[i];
        }
        // Only reachable through float rounding at the very top of the range.
        return items[items.length - 1];
    }
    /** A shuffled copy (Fisher-Yates). The input is never mutated. */
    shuffle(items) {
        const out = items.slice();
        for (let i = out.length - 1; i > 0; i -= 1) {
            const j = this.int(0, i + 1);
            const swap = out[i];
            out[i] = out[j];
            out[j] = swap;
        }
        return out;
    }
}
/**
 * Lazily forks and caches the handles for one `advance` call.
 *
 * Streams appear in world state the first time something draws from them,
 * which means the set of streams is itself deterministic for a given seed and
 * intent script. (World hashing sorts keys, so insertion order never leaks
 * into a hash — see `hash.ts`.)
 */
export class RngRegistry {
    worldSeed;
    streams;
    handles = new Map();
    constructor(worldSeed, streams) {
        this.worldSeed = worldSeed;
        this.streams = streams;
    }
    get(name) {
        const cached = this.handles.get(name);
        if (cached)
            return cached;
        let state = this.streams[name];
        if (!state) {
            state = forkStream(this.worldSeed, name);
            this.streams[name] = state;
        }
        const handle = new Rng(name, state);
        this.handles.set(name, handle);
        return handle;
    }
}
//# sourceMappingURL=rng.js.map